import {
  LayersOutlined,
  VisibilityOffOutlined,
  Visibility,
} from "@mui/icons-material";
import { TreeItem } from "@mui/x-tree-view";
import { Box, IconButton } from "@mui/material";
import React, { Dispatch, SetStateAction } from "react";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { Topics } from "shared/common/stores/sidebarStore";
import { LayertreeLayers } from "./LayertreeLayers";
import {
  MagnetFilled,
  MagnetOutline,
} from "../../../../../common/utils/icons/icons";
import { BlockedLayer } from "../../../../../common/utils/icons/icons";

interface props {
  changeExportFile: (id: string, visible: boolean) => void;
  item: Topics;
  indexInput: number[];
  layerMenuID: string;
  setLayerMenuID: Dispatch<SetStateAction<string>>;
  handleCloseEverythingOnRemoveLayer: () => void;
}

export const LayertreeGroups = ({
  changeExportFile,
  item,
  indexInput,
  layerMenuID,
  setLayerMenuID,
  handleCloseEverythingOnRemoveLayer,
}: props) => {
  const apm = defaultStore((state) => state.apm);
  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);

  const handleVisibilityToggle = (enableVisibility: boolean) => {
    if (viewer === undefined) return;
    item.children?.forEach((element: Topics) => {
      if (element.layer) {
        element.layer.setVisible(enableVisibility);
        changeExportFile(element.layer.id, enableVisibility);
      }
    });
  };

  const handleSnappingToggle = (enableSnapping: boolean) => {
    if (viewer === undefined) return;
    item.children?.forEach((element: Topics) => {
      if (element.layer) {
        element.layer.setSnapable(enableSnapping);
      }
    });
  };

  const defaultColor = darkTheme ? "white" : "darkblue.main";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "start",
        position: "relative",
      }}
    >
      <TreeItem
        nodeId={item.key ? item.key : ""}
        label={item.name}
        icon={item.addedFromUser ? <LayersOutlined /> : <BlockedLayer />}
        sx={{
          flex: 1,
          "& > .MuiTreeItem-content": {
            // ← '>' limits to direct child only
            "@media (hover: hover)": {
              "&:hover .MuiTreeItem-iconContainer": {
                color: "secondary.main",
              },
              "&:hover .MuiTreeItem-label": {
                color: "secondary.main",
              },
            },
            "& .MuiTreeItem-iconContainer": {
              color: defaultColor,
            },
            "& .MuiTreeItem-label": {
              color: defaultColor,
            },
          },
        }}
      >
        {item.children &&
          item.children.map((children: Topics, index: number) => (
            <React.Fragment key={children.key}>
              {children.folder && (
                <LayertreeGroups
                  changeExportFile={changeExportFile}
                  item={children}
                  indexInput={[index]}
                  layerMenuID={layerMenuID}
                  setLayerMenuID={setLayerMenuID}
                  handleCloseEverythingOnRemoveLayer={
                    handleCloseEverythingOnRemoveLayer
                  }
                />
              )}
              {!children.folder && (
                <LayertreeLayers
                  changeExportFile={changeExportFile}
                  children={children}
                  index={[...indexInput, index]}
                  layerMenuID={layerMenuID}
                  setLayerMenuID={setLayerMenuID}
                  length={item.children?.length || 0}
                  handleCloseEverythingOnRemoveLayer={
                    handleCloseEverythingOnRemoveLayer
                  }
                />
              )}
            </React.Fragment>
          ))}
      </TreeItem>
      <Box
        sx={{
          position: "absolute",
          right: 0,
          marginTop: "1px",
        }}
      >
        <IconButton
          onClick={() =>
            monitor(
              apm,
              "change-snapping-layergroup | Layertree",
              () => handleSnappingToggle(!item.snappingEnabled),
              { layername: item.name, visible: !item.snappingEnabled }
            )
          }
          disableRipple={true}
          sx={{
            padding: 0,
            paddingRight: "2px",
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
        >
          {item.snappingEnabled ? <MagnetFilled /> : <MagnetOutline />}
        </IconButton>
        <IconButton
          onClick={() =>
            monitor(
              apm,
              "change-visibility-layergroup | Layertree",
              () => handleVisibilityToggle(!item.visible),
              { layername: item.name, visible: !item.visible }
            )
          }
          disableRipple={true}
          sx={{
            padding: 0,
            color: darkTheme ? "white" : "darkblue.main",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
        >
          {item.visible ? (
            <Visibility fontSize="small" />
          ) : (
            <VisibilityOffOutlined fontSize="small" />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};
